import React, { useEffect, useState } from "react";
import { X, Plus, Edit2, Trash2, Upload } from "lucide-react";
import api, { img, money } from "../../lib/api";
import { AdminHeader } from "./AdminLayout";
import { toast } from "sonner";

const empty = {
  name: "", subtitle: "", slug: "", category: "face", subcategory: "", short: "", description: "",
  price: 0, mrp: 0, size: "", sku: "", is_published: true, is_bestseller: false, is_new: false,
  images: [], collections: [], concerns: [], tags: [], benefits: [], how_to_use: [], key_actives: [],
  ingredients: "", suitable_for: "", texture: "", inventory: { available: 0, low_stock_threshold: 10 },
  story: "", meghalaya_inspiration: "", seo_title: "", seo_description: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then(r => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ ...empty });
  const openEdit = (p) => setEdit({ ...empty, ...p });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...edit };
      ["price", "mrp"].forEach(k => payload[k] = Number(payload[k]));
      if (payload.id) await api.put(`/admin/products/${payload.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); load(); };

  const uploadImg = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit({ ...edit, images: [...(edit.images || []), r.data.url] }); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  const listField = (label, key, placeholder) => (
    <div><label className="text-xs uppercase tracking-wide text-forest/55">{label}</label>
      <textarea className="w-full border border-sage/40 bg-white px-3 py-2 text-sm mt-1 rounded-sm" rows={2} placeholder={placeholder}
        value={(edit[key] || []).join("\n")} onChange={(e) => setEdit({ ...edit, [key]: e.target.value.split("\n").filter(Boolean) })} /></div>
  );

  return (
    <div>
      <AdminHeader title="Products" subtitle={`${products.length} products`}
        action={<button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="product-new"><Plus size={15} /> New Product</button>} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-sage/30 rounded-sm overflow-hidden" data-testid={`admin-product-${p.slug}`}>
            <div className="flex gap-3 p-3">
              <img src={img((p.images || [])[1] || p.images?.[0])} alt="" className="h-20 w-16 object-cover bg-beige/40" />
              <div className="flex-1 min-w-0">
                <div className="serif text-lg leading-tight truncate">{p.name}</div>
                <div className="text-xs text-forest/50">{money(p.price)} · Stock {p.inventory?.available ?? 0}</div>
                <div className="flex gap-1 mt-1">
                  {p.is_published ? <span className="text-[10px] bg-moss/20 text-moss px-1.5">Live</span> : <span className="text-[10px] bg-beige px-1.5">Draft</span>}
                  {p.is_bestseller && <span className="text-[10px] bg-forest text-ivory px-1.5">Best</span>}
                </div>
              </div>
            </div>
            <div className="flex border-t border-sage/20">
              <button onClick={() => openEdit(p)} className="flex-1 py-2 text-xs flex items-center justify-center gap-1 hover:bg-beige/50" data-testid={`edit-${p.slug}`}><Edit2 size={12} /> Edit</button>
              <button onClick={() => del(p)} className="flex-1 py-2 text-xs flex items-center justify-center gap-1 hover:bg-beige/50 text-brown border-l border-sage/20"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-forest/40" onClick={() => setEdit(null)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-ivory overflow-auto p-6">
            <div className="flex items-center justify-between mb-6"><h2 className="serif text-3xl">{edit.id ? "Edit Product" : "New Product"}</h2><button onClick={() => setEdit(null)}><X size={22} /></button></div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <F label="Name" v={edit.name} on={(v) => setEdit({ ...edit, name: v })} testid="pf-name" />
                <F label="Subtitle" v={edit.subtitle} on={(v) => setEdit({ ...edit, subtitle: v })} />
                <F label="Slug" v={edit.slug} on={(v) => setEdit({ ...edit, slug: v })} />
                <div><label className="text-xs uppercase tracking-wide text-forest/55">Category</label>
                  <select className="w-full border border-sage/40 bg-white px-3 py-2 text-sm mt-1 rounded-sm" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>
                    {["face", "hair", "body", "wellness"].map(c => <option key={c}>{c}</option>)}</select></div>
                <F label="Subcategory" v={edit.subcategory} on={(v) => setEdit({ ...edit, subcategory: v })} />
                <F label="SKU" v={edit.sku} on={(v) => setEdit({ ...edit, sku: v })} />
                <F label="Price (₹)" type="number" v={edit.price} on={(v) => setEdit({ ...edit, price: v })} testid="pf-price" />
                <F label="MRP (₹)" type="number" v={edit.mrp} on={(v) => setEdit({ ...edit, mrp: v })} />
                <F label="Size" v={edit.size} on={(v) => setEdit({ ...edit, size: v })} />
                <F label="Stock" type="number" v={edit.inventory?.available} on={(v) => setEdit({ ...edit, inventory: { ...edit.inventory, available: Number(v) } })} />
              </div>
              <F label="Short description" v={edit.short} on={(v) => setEdit({ ...edit, short: v })} />
              <div><label className="text-xs uppercase tracking-wide text-forest/55">Description</label>
                <textarea className="w-full border border-sage/40 bg-white px-3 py-2 text-sm mt-1 rounded-sm" rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              {listField("Benefits (one per line)", "benefits")}
              {listField("How to use (one per line)", "how_to_use")}
              {listField("Key actives (one per line)", "key_actives")}
              {listField("Collections (slugs, one per line)", "collections", "best-sellers")}
              {listField("Concerns (one per line)", "concerns", "Hydration")}
              <F label="Ingredients" v={edit.ingredients} on={(v) => setEdit({ ...edit, ingredients: v })} />
              <F label="Suitable for" v={edit.suitable_for} on={(v) => setEdit({ ...edit, suitable_for: v })} />
              <F label="Texture" v={edit.texture} on={(v) => setEdit({ ...edit, texture: v })} />

              <div>
                <label className="text-xs uppercase tracking-wide text-forest/55">Images</label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {(edit.images || []).map((u, i) => (
                    <div key={i} className="relative"><img src={img(u)} alt="" className="h-16 w-14 object-cover bg-beige/40" />
                      <button onClick={() => setEdit({ ...edit, images: edit.images.filter((_, k) => k !== i) })} className="absolute -top-2 -right-2 bg-brown text-ivory rounded-full h-5 w-5 flex items-center justify-center text-xs">×</button></div>
                  ))}
                  <label className="h-16 w-14 border border-dashed border-sage/50 flex items-center justify-center cursor-pointer hover:bg-beige/40"><Upload size={16} /><input type="file" accept="image/*" className="hidden" onChange={uploadImg} data-testid="pf-upload" /></label>
                </div>
              </div>

              <div className="flex gap-6 py-2">
                {[["is_published", "Published"], ["is_bestseller", "Bestseller"], ["is_new", "New"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.checked })} className="accent-forest" data-testid={`pf-${k}`} /> {l}</label>
                ))}
              </div>

              <button onClick={save} disabled={saving} className="btn-primary w-full" data-testid="pf-save">{saving ? "Saving…" : "Save Product"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, v, on, type = "text", testid }) => (
  <div><label className="text-xs uppercase tracking-wide text-forest/55">{label}</label>
    <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full border border-sage/40 bg-white px-3 py-2 text-sm mt-1 rounded-sm" /></div>
);
